import { type HSLColor } from "../api";

const CLASSNAME_PREFIX = "rewardPlugin-floating-toggle";

export class FloatingButton {
  private $container: HTMLDivElement;
  private $button: HTMLButtonElement;
  private $badge: HTMLSpanElement;

  constructor(
    private state: {
      visible: boolean;
      badge: number;
      color: HSLColor;
      variant: "tab" | "circular";
      placement: "bottom-right" | "bottom-left" | "top-right" | "top-left";
      positionPixels: number;
    },
  ) {
    // reset inital state
    this.state = { ...state, visible: false, badge: 0 };

    const cntr = (this.$container = document.createElement("div"));
    cntr.id = `${CLASSNAME_PREFIX}-container`;
    cntr.style.display = "none";

    const btn = (this.$button = document.createElement("button"));

    btn.className = `${CLASSNAME_PREFIX}-btn`;
    btn.dataset.visible = "false";
    btn.type = "button";

    btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="width:24px"><rect x="3" y="8" width="18" height="4" rx="1"/><path d="M12 8v13"/><path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"/><path d="M7.5 8a2.5 2.5 0 0 1 0-5A4.8 8 0 0 1 12 8a4.8 8 0 0 1 4.5-5 2.5 2.5 0 0 1 0 5"/></svg>`;

    const badge = (this.$badge = document.createElement("span"));
    badge.className = `${CLASSNAME_PREFIX}-badge`;
    badge.style.display = "none";
    badge.textContent = "0";

    btn.append(badge);
    cntr.append(btn);

    const style = document.createElement("style");
    style.textContent =
      // reset CSS
      `#${cntr.id}{font-family:sans-serif;font-size:16px;box-sizing:border-box}` +
      `#${cntr.id} *,#${cntr.id} *::before,#${cntr.id} *::after{box-sizing:inherit}` +
      // floating tab components
      `#${cntr.id} .${btn.className}{position:fixed;z-index:9999;transition:all 0.3s,outline 0.1s;border:0;display:flex;align-items:center;background:green;color:white;cursor:pointer;padding:6px;opacity:0}` +
      `#${cntr.id} .${btn.className}[data-side=right]{box-shadow:0 0 10px rgba(0,0,0,0.2);margin-right:-50px}` +
      `#${cntr.id} .${btn.className}[data-side=left]{box-shadow:0 0 -10px rgba(0,0,0,0.2);margin-left:-50px}` +
      `#${cntr.id} .${btn.className}[data-variant=tab][data-side=right]{right:0;border-radius:6px 0 0 6px}` +
      `#${cntr.id} .${btn.className}[data-variant=tab][data-side=left]{left:0;border-radius:0 6px 6px 0}` +
      `#${cntr.id} .${btn.className}[data-variant=circular]{border-radius:50%;outline:0 solid currentcolor}` +
      `#${cntr.id} .${btn.className}[data-variant=circular][data-side=right]{right:10px}` +
      `#${cntr.id} .${btn.className}[data-variant=circular][data-side=left]{left:10px}` +
      `#${cntr.id} .${btn.className}[data-variant=tab][data-side=right]:hover{padding-right:10px}` +
      `#${cntr.id} .${btn.className}[data-variant=tab][data-side=left]:hover{padding-left:10px}` +
      `#${cntr.id} .${btn.className}[data-variant=circular]:hover{outline-width:2px}` +
      `#${cntr.id} .${btn.className}[data-visible=true]{opacity:1;margin:0}` +
      `#${cntr.id} .${badge.className}{position:absolute;background:red;color:white;border-radius:50%;min-width:1.4em;line-height:1.4em;text-align:center;font-size:10px}` +
      `#${cntr.id} .${btn.className}[data-variant=tab] .${badge.className}{top:1px}` +
      `#${cntr.id} .${btn.className}[data-variant=circular] .${badge.className}{top:4px}` +
      `#${cntr.id} .${btn.className}[data-variant=tab][data-side=right] .${badge.className}{left:1px;transform:translate(-50%,-50%)}` +
      `#${cntr.id} .${btn.className}[data-variant=tab][data-side=left] .${badge.className}{right:1px;transform:translate(50%,-50%)}` +
      `#${cntr.id} .${btn.className}[data-variant=circular][data-side=right] .${badge.className}{left:4px;transform:translate(-50%,-50%)}` +
      `#${cntr.id} .${btn.className}[data-variant=circular][data-side=left] .${badge.className}{right:4px;transform:translate(50%,-50%)}`;
    cntr.append(style);

    document.body.append(cntr);

    this.update(state);
  }

  onClick(callback: (event: MouseEvent) => void) {
    this.$button.addEventListener("click", callback);
  }

  // offClick(callback: (event: MouseEvent) => void) {
  //   this.$button.removeEventListener("click", callback);
  // }

  // current() {
  //   return this.state;
  // }

  update(state: Partial<typeof this.state>) {
    const prevState = this.state;
    this.state = { ...this.state, ...state };

    const sideCorner = this.state.placement.split("-");

    Object.assign(this.$button.dataset, {
      variant: this.state.variant,
      corner: sideCorner[0],
      side: sideCorner[1],
    });

    Object.assign(this.$button.style, {
      backgroundColor: `hsl(${this.state.color[0]} ${this.state.color[1]}% ${this.state.color[2]}%)`,
      top: "",
      bottom: "",
      [sideCorner[0]!]: `${this.state.positionPixels}px`,
    });

    if (prevState.visible !== this.state.visible) {
      setTimeout(() => {
        this.$button.dataset.visible = String(this.state.visible);
      }, 0);

      if (this.state.visible) {
        this.$container.style.display = "";
      } else {
        setTimeout(() => {
          this.$container.style.display = "none";
        }, 500);
      }
    }

    this.$badge.textContent = String(this.state.badge);
    this.$badge.style.display = this.state.badge ? "" : "none";
  }

  deregister() {
    this.$container.remove();
  }
}
